import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import { Button, DialogActions, DialogTitle, Chip } from "@material-ui/core";
import TagsInput from "react-tagsinput";
import classNames from "classnames";
import { toLower, trim, uniqBy, debounce } from "lodash";

import { BeautifulDialog, DialogContent } from "js/react/components/Dialogs/BaseDialog";
import Icon from "js/react/components/Icon";
import { caseInsensitiveSort } from "js/core/utilities/utilities";
import { TAG_DELIMITERS } from "js/react/constants";
import { Key } from "js/core/utilities/keys";

const MAX_TAGS = 50;
const ADD_TAG_DELIMITERS = TAG_DELIMITERS.filter(key => key !== Key.ENTER);
// shortcut
const normalizeTag = tag => toLower(trim(tag));
const sanitizeTag = tag => tag
    .replaceAll(".", "-")
    .replaceAll("#", "-")
    .replaceAll("$", "-")
    .replaceAll("/", "-")
    .replaceAll("[", "-")
    .replaceAll("]", "-");

class AddTagsDialog extends Component {
    inputRef = React.createRef();

    constructor(props) {
        super(props);

        const {
            activeTags = [],
            existingTags = [],
        } = props;

        this.state = {
            value: "",
            newTags: activeTags.map(normalizeTag),
            existingTags: existingTags.map(normalizeTag),
        };
    }

    componentDidMount() {
        setTimeout(() => {
            this.inputRef && this.inputRef.current.focus();
        }, 500);
    }

    getAvailableTags() {
        let { existingTags, newTags, filter = "" } = this.state;
        const { length: searchLength } = filter;

        const pendingTags = newTags.map(normalizeTag);
        const available = [];
        const disabled = [];

        // filter out some tags
        filter = toLower(filter);
        existingTags = existingTags.forEach(tag => {
            const find = normalizeTag(tag);
            const matchesFilter = find.substr(0, searchLength) === filter;
            const alreadyIncluded = pendingTags.includes(find);
            const target = matchesFilter && !alreadyIncluded ? available : disabled;
            target.push(tag);
        });

        // lastly, sort the tags
        caseInsensitiveSort(available, normalizeTag);
        caseInsensitiveSort(disabled, normalizeTag);

        // give back the tags to work with
        return { available, disabled };
    }

    updateTags = debounce(tags => {
        const newTags = this.getMergedTags(tags);

        this.setState({
            newTags,
            filter: "",
        });
    }, 100)

    getMergedTags = tags => {
        const { existingTags } = this.state;

        // replace any input tags with matching values from existing tags
        let newTags = tags
            .map(sanitizeTag)
            .map(tag => {
                const find = normalizeTag(tag);
                return existingTags.find(tag => normalizeTag(tag) === find) || tag;
            })
            .filter(tag => normalizeTag(tag));

        return uniqBy([...this.state.newTags, ...newTags], normalizeTag);
    }

    handleAccept = extraTags => {
        const { acceptCallback, closeDialog } = this.props;

        this.updateTags.flush();
        acceptCallback && acceptCallback(this.getMergedTags(extraTags));
        closeDialog();
    }

    handleRemoveTag = tagIndex => {
        const newTags = [...this.state.newTags];
        newTags.splice(tagIndex, 1);
        this.setState({ newTags });
    }

    handleChangeTags = tags => {
        this.updateTags(tags);
    };

    handleExistingTagClick = tag => {
        this.updateTags([...this.state.newTags, tag]);

        // refocus after a click
        const { current: tagsInput } = this.inputRef;
        tagsInput.input.focus();
        tagsInput.input.select();
    }

    handleFilterTags = event => {
        const { value: filter } = event.target;
        this.setState({ filter });
    }

    handleInputKeyDown = (event, onKeyDown) => {
        if ((event.key === "Delete" || event.key === "Backspace") && !event.target.value && this.state.newTags.length) {
            event.preventDefault();
            this.handleRemoveTag(this.state.newTags.length - 1);
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            this.handleAccept(event.target.value ? [event.target.value] : []);
            return;
        }

        onKeyDown && onKeyDown(event);
    }

    renderInput = ({ addTag, ...props }) => {
        const { classes } = this.props;
        //explicitly differentiating className from other so we can overwrite the default class from react-tagsinput
        const { className, onKeyDown, ...other } = props;
        return (
            <input
                className={classes.input}
                style={{ flexGrow: 1 }}
                type="text"
                {...other}
                onKeyDown={event => this.handleInputKeyDown(event, onKeyDown)}
                onKeyUp={this.handleFilterTags}
            />
        );
    }

    render() {
        const { classes } = this.props;
        const { newTags } = this.state;

        const tags = this.getAvailableTags();
        const hasTags = !!(tags.available.length || tags.disabled.length);

        return (<BeautifulDialog closeDialog={this.props.closeDialog} >
            <DialogTitle className={classes.title}>Add tags</DialogTitle>
            <DialogContent className={classes.content}>
                <TagsInput
                    addKeys={ADD_TAG_DELIMITERS}
                    className={classes.tagsInput}
                    addOnBlur
                    addOnPaste
                    onlyUnique
                    maxTags={MAX_TAGS}
                    inputProps={{
                        placeholder: "Type a tag and press Space..."
                    }}
                    value={newTags}
                    ref={this.inputRef}
                    pasteSplit={this.pasteSplit}
                    onChange={this.handleChangeTags}
                    renderTag={props => <StyledTag {...props} keyProp={props.key} onRemove={this.handleRemoveTag} />}
                    renderInput={this.renderInput}
                    renderLayout={(tagComponents, inputComponent) => {
                        return (
                            <div className={classes.tagLayout}>
                                {tagComponents}
                                {inputComponent}
                            </div>
                        );
                    }}
                />
                {hasTags ? (
                    <div className={classes.existingTags}>
                        <StyledTagsList
                            availableTags={tags.available}
                            disabledTags={tags.disabled}
                            handleClick={this.handleExistingTagClick}
                        />
                    </div>)
                    : null}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        this.props.closeDialog();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        this.handleAccept([]);
                    }}
                    color="primary"
                >
                    Add tags
                </Button>
            </DialogActions>
        </BeautifulDialog>);
    }
}

const StyleAddTagsDialog = withStyles({
    title: {
        paddingBottom: 18
    },
    content: {
        paddingBottom: 24
    },
    tagsInput: {
        flexGrow: 2,
        padding: "8px 10px 4px",
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        borderRadius: 2,
        overflow: "hidden",
        fontSize: 13,
        minHeight: 44
    },
    tagLayout: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: -6
    },
    input: {
        height: 26,
        marginBottom: 6,
        background: "transparent",
        border: 0,
        color: "rgb(34, 34, 34)",
        fontFamily: "Source Sans Pro",
        fontWeight: 500,
        outline: "none",
        padding: "3px 4px",
        fontSize: "14px",
        width: 100
    },
    existingTags: {
        marginTop: 12
    }
})(AddTagsDialog);

function TagsList({ tags = [], availableTags = [], disabledTags = [], classes, handleClick }) {
    const activeTags = [...tags, ...availableTags];

    return (
        <div>
            {[activeTags, disabledTags].map((items, index) => {
                const disabled = index === 1;

                return items.map((tag, i) => {
                    return (<Chip
                        key={i}
                        color="default"
                        label={tag}
                        clickable
                        disabled={disabled}
                        onClick={handleClick.bind(this, tag)}
                        classes={{ root: disabled ? classes.tagsRootDisabled : classes.tagsRoot }}
                    />);
                });
            })}
        </div>
    );
}

const tagsRoot = {
    height: 26,
    borderRadius: "4px",
    background: "white",
    color: "#444",
    margin: "0 6px 6px 0",
    border: "#c6c6c6 solid 1px",
    fontSize: 13,
    fontWeight: 500,
    "& .MuiChip-label": {
        paddingLeft: 10,
        paddingRight: 10,
        lineHeight: "26px"
    }
};

const StyledTagsList = withStyles({
    tagsRoot,
    tagsRootDisabled: {
        ...tagsRoot,
        background: "#fafafa",
        color: "#999",
        border: "#ccc solid 1px"
    }
})(TagsList);

const Tag = props => {
    const { tag, keyProp, onRemove, getTagDisplayValue, classes } = props;

    return (
        <Chip
            key={keyProp}
            tabIndex={0}
            color="primary"
            className={classNames(classes.tag)}
            label={getTagDisplayValue(tag)}
            onDelete={() => onRemove(keyProp)}
            deleteIcon={<Icon small light iconName="close" />}
        />
    );
};

const StyledTag = withStyles({
    tag: {
        height: 26,
        marginRight: 6,
        marginBottom: 6,
        color: "white",
        fontWeight: 600,
        fontSize: 13,
        paddingRight: 6,
        "& .MuiChip-label": {
            paddingLeft: 10,
            paddingRight: 8,
            lineHeight: "26px"
        },
        "& .MuiChip-deleteIcon": {
            width: 13,
            height: 13,
            margin: "0 7px 0 -3px",
            fontSize: 13,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer"
        }
    }
})(Tag);

export default StyleAddTagsDialog;
